import React, { useMemo } from 'react'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'

import AddressInput from './AddressInput'
import DateRangePicker from 'components/common/DateRangePicker'
import FooterButtons from './FooterButtons'
import usePostingNoticeRequestContext from 'hooks/posting_notice_request/usePostingNoticeRequestContext'
import isEmptyObject from 'helpers/node/isEmptyObject'

const textFieldProps = {
  size: 'small',
  fullWidth: true,
}

// Generate validation schema based on renderingData
const getValidationSchema = (renderingData, addressMapping) => {
  const schemaFields = {}

  renderingData.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type === 'address') {
        // Add validation for address fields based on address mapping
        const mapping = addressMapping[field.name]
        schemaFields[mapping.address.key] = Yup.string()
        schemaFields[mapping.city.key] = Yup.string()
        schemaFields[mapping.state.key] = Yup.string()
        schemaFields[mapping.zip.key] = Yup.string()
      } else {
        let fieldSchema = Yup.string()

        if (field.required) {
          fieldSchema = fieldSchema.required(`${field.label} is required`)
        }

        // Add specific validation based on field type
        if (field.type === 'date') {
          fieldSchema = fieldSchema.test(
            'is-date',
            'Please enter a valid date',
            (value) => !value || !isNaN(Date.parse(value))
          )
        }

        schemaFields[field.name] = fieldSchema
      }
    })
  })

  return Yup.object().shape(schemaFields)
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h1" fontWeight="bold">
        {title}
      </Typography>
      <Divider sx={{ my: 3 }} />
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  )
}

function Field({ field }) {
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    errors,
    touched,
    setFieldTouched,
  } = useFormikContext()
  const { generalInformationAddressMapping: addressMapping } =
    usePostingNoticeRequestContext()

  if (field.type === 'address') {
    const addressKeys = {
      address: addressMapping[field.name].address.key,
      city: addressMapping[field.name].city.key,
      state: addressMapping[field.name].state.key,
      zip: addressMapping[field.name].zip.key,
    }

    return (
      <AddressInput
        ignoreCountry
        addressKeys={addressKeys}
        labels={{
          address: addressMapping[field.name].address.label,
          city: addressMapping[field.name].city.label,
          state: addressMapping[field.name].state.label,
          zip: addressMapping[field.name].zip.label,
        }}
      />
    )
  }

  if (field.type === 'date') {
    return (
      <Grid item xs={field.cols || 6}>
        <DateRangePicker
          buttonConfig={[7, 8, 9, 10]}
          single
          value={[values[field.name]]}
          setValue={(value) => {
            setFieldTouched(field.name, true)
            setFieldValue(
              field.name,
              Array.isArray(value) ? value.find((v) => v) : value
            )
          }}
          placeholder={field.label}
          label={field.label}
          error={touched[field.name] && Boolean(errors[field.name])}
          helperText={touched[field.name] && errors[field.name]}
          required={field.required}
          {...textFieldProps}
        />
      </Grid>
    )
  }

  return (
    <Grid item xs={field.cols || 6}>
      <TextField
        key={field.name}
        label={field.label}
        name={field.name}
        type={field.type}
        required={field.required}
        value={values[field.name] || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched[field.name] && Boolean(errors[field.name])}
        helperText={touched[field.name] && errors[field.name]}
        {...textFieldProps}
      />
    </Grid>
  )
}

function GeneralInformation() {
  const {
    stage,
    setStage,
    generalInformation,
    setGeneralInformation,
    generalInformationRenderingData,
    generalInformationAddressMapping,
  } = usePostingNoticeRequestContext()

  const initialValues = useMemo(() => {
    const values = {}

    generalInformationRenderingData.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type === 'address') {
          const mapping = generalInformationAddressMapping[field.name]
          values[mapping.address.key] = ''
          values[mapping.city.key] = ''
          values[mapping.state.key] = ''
          values[mapping.zip.key] = ''
        } else {
          values[field.name] = ''
        }
      })
    })

    return values
  }, [generalInformationRenderingData, generalInformationAddressMapping])

  return (
    <Formik
      initialValues={
        isEmptyObject(generalInformation) ? initialValues : generalInformation
      }
      validationSchema={getValidationSchema(
        generalInformationRenderingData,
        generalInformationAddressMapping
      )}
      onSubmit={(values) => {
        setStage(stage + 1)
        setGeneralInformation(values)
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box>
          <Card sx={{ p: 6, width: '100%' }}>
            <Stack spacing={4}>
              {generalInformationRenderingData.map((section) => (
                <>
                  <Section key={section.title} title={section.title}>
                    {section.fields.map((field) => (
                      <Field key={field.name} field={field} />
                    ))}
                  </Section>
                </>
              ))}
            </Stack>
          </Card>
          <FooterButtons />
        </Box>
      </Box>
    </Formik>
  )
}

export default GeneralInformation
